import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Home, Menu, ShoppingBag, Receipt, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

const API_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8081';

const OrdersPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const restaurantId = searchParams.get('r');
  const tableId = searchParams.get('t');
  
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restaurant, setRestaurant] = useState(null);
  const [table, setTable] = useState(null);
  const [customerPhone, setCustomerPhone] = useState(null);
  const [hasActiveSession, setHasActiveSession] = useState(false);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  // Handle swipe gestures for page navigation
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    setTouchEnd(e.changedTouches[0].clientX);
  };

  // Detect swipe and navigate
  useEffect(() => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      // Swipe left - go to Pay Bill
      navigate(`/pay-bill?r=${restaurantId}&t=${tableId}`);
    } else if (isRightSwipe) {
      // Swipe right - go back to Menu
      navigate(`/menu?r=${restaurantId}&t=${tableId}`);
    }
  }, [touchStart, touchEnd, navigate, restaurantId, tableId]);

  useEffect(() => {
    if (!restaurantId || !tableId) {
      toast.error('Invalid restaurant or table');
      navigate('/');
      return;
    }
    
    // Staff order page - no customer session needed
    // Fetch all table orders (both customer and staff orders)
    fetchOrders();
    fetchRestaurantInfo();
    fetchTableInfo();
  }, [restaurantId, tableId]);

  const fetchRestaurantInfo = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/restaurants/${restaurantId}/public-info`);
      setRestaurant(response.data);
    } catch (error) {
      console.error('Failed to fetch restaurant info:', error);
    }
  };

  const fetchTableInfo = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/restaurants/${restaurantId}/tables/${tableId}`);
      setTable(response.data);
    } catch (error) {
      console.error('Failed to fetch table info:', error);
    }
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      
      console.log('Fetching all orders for table:', tableId, 'restaurant:', restaurantId);
      
      // Fetch all orders for this table (both customer and staff orders)
      const response = await axios.get(
        `${API_URL}/api/restaurants/${restaurantId}/qr-orders/table/${tableId}/all`
      );
      
      if (response.data && response.data.length > 0) {
        console.log('Found table orders:', response.data);
        // Filter out cancelled and completed orders (staff should only see active orders)
        const activeOrders = response.data.filter(order => 
          order.orderStatus !== 'CANCELLED' && order.orderStatus !== 'COMPLETED'
        );
        setOrders(activeOrders);
        setHasActiveSession(activeOrders.length > 0); // Enable session if any active orders exist
      } else {
        console.log('No orders found for this table');
        setOrders([]);
        setHasActiveSession(false);
      }
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      setOrders([]);
      setHasActiveSession(false);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'PENDING': return 'bg-yellow-100 text-yellow-800';
      case 'CONFIRMED': return 'bg-blue-100 text-blue-800';
      case 'PREPARING': return 'bg-orange-100 text-orange-800';
      case 'READY': return 'bg-green-100 text-green-800';
      case 'DELIVERED': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div 
      className="min-h-screen bg-gray-50"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 via-orange-600 to-orange-700 shadow-lg sticky top-0 z-10">
        <div className="max-w-md mx-auto px-4 py-3">
          <p className="mb-1 text-center text-[10px] sm:text-xs font-bold text-orange-100/95 tracking-wide">
            Powered by Trivana Hospitality
          </p>
          <div className="flex items-center justify-start">
            <div>
              <h1 className="text-xl font-bold text-white">
                {restaurant?.name || 'Restaurant'}
              </h1>
              <p className="text-sm text-orange-100">
                Table {table?.tableNumber || tableId}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Orders List */}
      <div className="max-w-md mx-auto px-4 py-4 pb-20">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Your Orders</h2>
            {hasActiveSession && customerPhone && (
              <p className="text-xs text-gray-500 mt-1">
                📱 {customerPhone} | {localStorage.getItem('customerName') || 'Customer'}
              </p>
            )}
          </div>
          <button 
            onClick={fetchOrders}
            className="text-sm text-orange-600 hover:text-orange-700 font-medium"
          >
            Refresh
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
            <p className="mt-4 text-gray-500">Loading orders...</p>
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-12">
            <ShoppingBag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            {hasActiveSession ? (
              <div>
                <p className="text-gray-500 mb-2">No orders found for this session</p>
                <p className="text-xs text-gray-400 mb-4">
                  Customer: {localStorage.getItem('customerPhone')}
                </p>
              </div>
            ) : (
              <p className="text-gray-500 mb-4">No orders yet</p>
            )}
            <button
              onClick={() => navigate(`/menu?r=${restaurantId}&t=${tableId}`)}
              className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
            >
              Browse Menu
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {orders.map((order) => (
              <div key={order.orderId} className="bg-white rounded-lg shadow-sm border border-gray-100 p-3 sm:p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="text-sm sm:text-base font-semibold text-gray-800">#{order.orderNumber}</p>
                    <p className="text-xs sm:text-sm text-gray-500">
                      {new Date(order.createdAt).toLocaleString('en-IN', {
                        dateStyle: 'medium',
                        timeStyle: 'short'
                      })}
                    </p>
                    {/* Show customer info if order is from customer */}
                    {order.customerName && (
                      <p className="text-xs text-blue-600 mt-1">
                        👤 {order.customerName} {order.customerPhone && `(${order.customerPhone})`}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-0.5 text-right">
                    <span className={`px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs font-medium ${getStatusColor(order.orderStatus)}`}>
                      {order.orderStatus}
                    </span>
                    {order.paymentStatus && order.paymentStatus !== order.orderStatus && (
                      <span className="text-xs text-gray-500">
                        Payment: {order.paymentStatus}
                      </span>
                    )}
                  </div>
                </div>

                <div className="space-y-1.5 mb-2">
                  {order.items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between text-xs sm:text-sm">
                      <span className="text-gray-600">
                        {item.quantity}x {item.itemName}
                      </span>
                      <span className="text-gray-800 font-medium">
                        ₹{item.totalPrice}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="pt-2 border-t border-gray-100">
                  <div className="flex justify-between items-center">
                    <span className="text-sm sm:text-base font-semibold text-gray-800">Total</span>
                    <span className="text-base sm:text-lg font-bold text-orange-600">
                      ₹{order.grandTotal}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200">
        <div className="max-w-md mx-auto px-3 py-2 flex justify-around items-center">
          <button 
            onClick={() => navigate('/tables')}
            className="flex flex-col items-center gap-0.5 text-gray-500 hover:text-orange-600"
          >
            <Home className="w-5 h-5" />
            <span className="text-xs">Home</span>
          </button>
          <button 
            onClick={() => navigate(`/menu?r=${restaurantId}&t=${tableId}`)}
            className="flex flex-col items-center gap-0.5 text-gray-500 hover:text-orange-600"
          >
            <Menu className="w-5 h-5" />
            <span className="text-xs">Menu</span>
          </button>
          <button 
            className="flex flex-col items-center gap-0.5 text-orange-600"
          >
            <ShoppingBag className="w-5 h-5" />
            <span className="text-xs font-medium">Orders</span>
          </button>
          <button 
            onClick={() => navigate(`/pay-bill?r=${restaurantId}&t=${tableId}`)}
            className="flex flex-col items-center gap-0.5 text-gray-500 hover:text-orange-600"
          >
            <Receipt className="w-5 h-5" />
            <span className="text-xs">Pay Bill</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrdersPage;
